import React, { createContext, ReactNode, useContext, useState } from 'react';
import useMobileScreen from '../hooks/useIsMobile';
import { AllCount, ApiResponseType, TransactionDetails } from './ContextType';
import {axiosInstance} from '../api';
import moment from 'moment';

type ITransactionsContext= {
  getTransactions: (payload: any) => void;
  transactionData: any;
  isTotalPages?: number;
  loading: boolean;
  txCountPerMinute: number;
  getTransactionDataPerMinute: () => void
  getTransactionDetails: (txnId: string) => void;
  transactionDetails?: any;
  txCount: number
}

const TransactionsContext = createContext<ITransactionsContext | undefined>(undefined);

export const useTransactionsContext = () => {
  const context = useContext(TransactionsContext);
  if (!context) {
    throw new Error("useTheme must be used within a CustomToastProvider");
  }
  return context;
};

type IContextProviderProps = {
  children: ReactNode;
};

export const TransactionsContextProvider: React.FC<IContextProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [transactionData, setTransactionData] = useState<any>();
  const [isTotalPages, setIsTotalPages] = useState<number>();
  const [transactionDetails, setTransactionDetails] = useState({});
  const [txCountPerMinute, setTxCountPerMinute] = useState<number>(0);
  const [txCount, setTxCount] = useState<number>(0);
  const mobile = useMobileScreen();

  const getTransactionDataPerMinute = async (): Promise<ApiResponseType<any>> => {
    try {
      setLoading(true);
      let url = `/metrics/txByLastMinute`;
      const response = await axiosInstance.get(url);
      setTxCountPerMinute(response?.data?.transaction_count);
      setLoading(false);
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getTransactions = async (payload: any): Promise<ApiResponseType<AllCount>> => {
    try {
      setLoading(true);
      const isoStartDate = payload?.blocksFromDate ? moment(payload?.blocksFromDate).format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      const isoEndDate = payload?.blocksToDate ? moment(payload?.blocksToDate).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]') :  null;

      let url = (isoStartDate && isoEndDate)
        ? `/txList?from=${isoStartDate}&to=${isoEndDate}&page=${payload?.PageNumber}&size=${payload?.Size}`
        : `/txList?page=${payload?.PageNumber}&size=${payload?.Size}`;

      if (payload?.search) {
        url += `&singleSearch=${payload?.search}`;
      }

      const response = await axiosInstance.get(url);
      if(mobile) {
        setTransactionData(payload?.PageNumber === 1 ? response?.data?.rows?.txnsData : [...transactionData, ...response?.data?.rows?.txnsData])
      } else {
        setTransactionData(response?.data?.rows?.txnsData);
      }
      setTxCount(Number(response?.data?.rows.countOfTxns))
      setIsTotalPages(response?.data?.rows?.noOfPages);
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getTransactionDetails = async (txnId: string): Promise<ApiResponseType<TransactionDetails>> => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/txList?txid=${txnId}`);
      setTransactionDetails(response?.data?.rows?.txnsData);
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  }

  const contextValue = {
    loading,
    getTransactions,
    transactionData,
    isTotalPages,
    txCountPerMinute,
    getTransactionDataPerMinute,
    getTransactionDetails,
    transactionDetails,
    txCount
  };

  return (
    <TransactionsContext.Provider value={contextValue}>{children}</TransactionsContext.Provider>
  );
};
